<div class="p-2">
  <!-- Header Section -->
  <div class="flex flex-wrap justify-between gap-2 items-center mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-800" i18n>Audit Logs</h1>
      <p class="text-gray-600" i18n>Monitor system activity and user actions across the platform.</p>
    </div>
    <div class="flex flex-col md:flex-row gap-3 md:items-center">
      <div class="flex flex-wrap items-center gap-2">
        <label class="text-sm font-medium text-gray-700" i18n>Format:</label>
        <p-select 
          [options]="exportFormatOptions" 
          [(ngModel)]="exportFormat" 
          placeholder="Select Format" i18n-placeholder
          optionLabel="label"
          optionValue="value"
          styleClass="min-w-[100px]">
        </p-select>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <label class="text-sm font-medium text-gray-700" i18n>Records:</label>
        <input 
          type="number" 
          [(ngModel)]="exportRecordCount" 
          min="1" 
          max="10000"
          placeholder="500" i18n-placeholder
          class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-20">
      </div>
      <p-button 
        label="Export" i18n-label
        icon="pi pi-download" 
        severity="secondary"
        (onClick)="exportLogs()"
        [disabled]="loading"
        [pTooltip]="'Export filtered system logs to ' + exportFormat.toUpperCase() + ' with specified number of records'"
        tooltipPosition="bottom">
      </p-button>
      <p-button 
        label="Refresh" i18n-label
        icon="pi pi-refresh" 
        (onClick)="loadLogs()"
        [disabled]="loading">
      </p-button>
    </div>
  </div>

  <!-- Search and Filters -->
  <p-card styleClass="mb-6">
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <span class="">
          <p-iconfield iconPosition="left">
            <p-inputicon>
                <i class="pi pi-search"></i>
            </p-inputicon>
            <input 
            pInputText 
            type="text" 
            placeholder="Search logs by description, action, or error message..." i18n-placeholder
            [(ngModel)]="searchText"
            (keyup.enter)="applyFilters()"
            class="min-w-[200px]" 
            fluid
            pTooltip="Search across log descriptions, actions, error messages, user names, and company names"
            tooltipPosition="top">
        </p-iconfield>
        </span>
      </div>
      
      <!-- Filters -->
      <div class="flex flex-wrap gap-3">
        <p-select 
          [options]="typeOptions" 
          [(ngModel)]="selectedType" 
          (ngModelChange)="applyFilters()"
          placeholder="Type: All" i18n-placeholder
          optionLabel="label"
          optionValue="value"
          styleClass="min-w-[150px]"
          pTooltip="Filter logs by action type (e.g., login, update, delete)"
          tooltipPosition="top">
        </p-select>
        
        <p-select 
          [options]="statusOptions" 
          [(ngModel)]="selectedStatus" 
          (ngModelChange)="applyFilters()"
          placeholder="Status: All" i18n-placeholder
          optionLabel="label"
          optionValue="value"
          styleClass="min-w-[150px]"
          pTooltip="Filter logs by operation status (Success or Fail)"
          tooltipPosition="top">
        </p-select>
        
        <p-select 
          [options]="actionOptions" 
          [(ngModel)]="selectedAction" 
          (ngModelChange)="applyFilters()"
          placeholder="Action: All" i18n-placeholder
          optionLabel="label"
          optionValue="value"
          styleClass="min-w-[180px]"
          pTooltip="Filter logs by system module or feature area"
          tooltipPosition="top">
        </p-select>
      </div>
    </div>

    <!-- Date Range Filters -->
    <div class="flex flex-wrap gap-4 mt-4">
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700" i18n>From:</label>
        <input 
          type="date" 
          [(ngModel)]="startDate" 
          placeholder="Start Date" i18n-placeholder
          class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          pTooltip="Select start date for filtering logs (time will be set to 00:00:00)"
          tooltipPosition="top">
      </div>
      
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700">To:</label>
        <input 
          type="date" 
          [(ngModel)]="endDate" 
          placeholder="End Date" i18n-placeholder

          class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          pTooltip="Select end date for filtering logs (time will be set to 23:59:59)"
          tooltipPosition="top">
      </div>
      
      <div class="flex gap-2">
        <p-button 
          label="Apply Filters" 
          i18n-label
          icon="pi pi-filter" 
          (onClick)="applyFilters()"
          [disabled]="loading"
          pTooltip="Apply current filter settings to search logs"
          tooltipPosition="top">
        </p-button>
        
        <p-button 
          label="Clear" i18n-label
          icon="pi pi-times" 
          severity="secondary"
          [outlined]="true"
          (onClick)="clearFilters()"
          [disabled]="loading"
          pTooltip="Reset all filters and show all logs"
          tooltipPosition="top">
        </p-button>
      </div>
    </div>
  </p-card>

  <!-- Logs Table -->
  <p-card>
    <p-table 
      [value]="logs" 
      [loading]="loading"
      [paginator]="false"
      [rows]="pageSize"
      [showCurrentPageReport]="false"
      [tableStyle]="{'min-width': '50rem'}"
      [sortMode]="'single'"
      (onSort)="onSort($event)"
      [sortField]="sortField"
      [sortOrder]="sortOrder"
      styleClass="p-datatable-sm">
      
      <!-- User Column -->
      <ng-template pTemplate="header">
        <tr>
          <th  style="width: 150px">
            User
            
          </th>
          <th  style="width: 150px">
            Company
            
          </th>
          <th  style="width: 120px">
            Action
          
          </th>
          <th style="width: 100px">
            Type
          
          </th>
          <th style="width: 100px">
            Status
            
          </th>
          <th  style="width: 120px">
            IP Address
            
          </th>
          <th pSortableColumn="timestamp" style="width: 150px">
            Timestamp
            <p-sortIcon field="timestamp"></p-sortIcon>
          </th>
          <th style="width: 100px">Duration</th>
        </tr>
      </ng-template>
      
      <ng-template pTemplate="body" let-log>
        <tr>
          <!-- User -->
          <td>
            <div class="flex items-center gap-2">
              <p-avatar 
                [label]="getUserInitials(log)" 
                size="normal"
                styleClass="bg-blue-100 text-blue-800">
              </p-avatar>
              <div>
                <div class="font-medium text-gray-900">{{ getUserDisplayName(log) }}</div>
                <div class="text-xs text-gray-500" *ngIf="log.userId?.refNo">
                  {{ log.userId.refNo }}
                </div>
              </div>
            </div>
          </td>
          
          <!-- Company -->
          <td>
            <span class="text-sm text-gray-700">{{ getCompanyName(log) }}</span>
          </td>
          
          <!-- Action -->
          <td>
            <span class="text-sm font-mono text-gray-600">{{ log.action }}</span>
          </td>
          
          <!-- Type -->
          <td>
            <p-tag 
              [value]="log.type" 
              [severity]="getTypeSeverity(log.type)"
              styleClass="text-xs">
            </p-tag>
          </td>
          
          <!-- Status -->
          <td>
            <p-tag 
              [value]="log.status" 
              [severity]="getStatusSeverity(log.status)"
              styleClass="text-xs">
            </p-tag>
          </td>
          
          <!-- IP Address -->
          <td>
            <span class="text-sm font-mono text-gray-600">{{ log.ipAddress }}</span>
          </td>
          
          <!-- Timestamp -->
          <td>
            <div class="text-sm text-gray-700">
              {{ log.timestamp | date:'dd/MM/yy HH:mm' }}
            </div>
            <!-- <div class="text-xs text-gray-500">
              {{ log.timestamp | date:'dd' }}s ago
            </div> -->
          </td>
          
          <!-- Duration -->
          <td>
            <span class="text-sm text-gray-600">{{ formatDuration(log.duration) }}</span>
          </td>
         
        </tr>
        <tr>
          <td colspan="8">
            <div class="bg-gray-100 p-2 rounded-lg">
              <div class="text-sm text-gray-700" [title]="log.description">
                {{ log.description }}
              </div>
              <div class="text-xs text-red-600 mt-1" *ngIf="log.errorMessage">
                {{ log.errorMessage }}
              </div>
            </div>
          </td>
        </tr>
      </ng-template>
      
      <!-- Empty State -->
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9" class="text-center py-8">
            <div class="text-gray-500">
              <i class="pi pi-info-circle text-2xl mb-2"></i>
              <p i18n>No logs found matching your criteria.</p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- Pagination -->
    <div class="flex flex-wrap justify-between items-center mt-4">
      <div class="text-sm text-gray-600">
        Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalLogs) }} of {{ totalLogs }} entries
      </div>
      
      <p-paginator 
        [rows]="pageSize"
        [totalRecords]="totalLogs"
        [rowsPerPageOptions]="pageSizes"
        (onPageChange)="onPageChange($event)"
        [showCurrentPageReport]="false">
      </p-paginator>
    </div>
  </p-card>

  <!-- Toast Messages -->
  <p-toast></p-toast>
  
  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>
</div>
